import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, Image, FlatList, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/providers/AuthProvider';
import { useMessages } from '@/modules/chat';
import { downloadFile } from '@/modules/chat/utils/fileUpload';
import { useChatPolls, PollWithDetails } from '@/modules/polls';
import { Loader } from '@/components/Loader';
import { Message } from '@/modules/chat/types';

type MediaTab = 'images' | 'videos' | 'files' | 'polls';

export default function ChatDetailScreen() {
  const router = useRouter();
  const { chatId } = useLocalSearchParams<{ chatId: string }>();
  const { user } = useAuth();
  const userId = user?.id;

  const [activeTab, setActiveTab] = useState<MediaTab>('images');
  const [downloading, setDownloading] = useState<string | null>(null);

  const { messages, loading: messagesLoading } = useMessages(chatId, userId);
  const { polls, loading: pollsLoading, activePolls, closedPolls } = useChatPolls(chatId, userId);

  const loading = activeTab === 'polls' ? pollsLoading : messagesLoading;

  // Filter messages by media type
  const mediaMessages = useMemo(() => {
    if (!messages) return { images: [], videos: [], files: [] };

    const images = messages.filter((m) => m.type === 'image' && !m.is_deleted);
    const videos = messages.filter((m) => m.type === 'video' && !m.is_deleted);
    const files = messages.filter((m) => m.type === 'file' && !m.is_deleted);

    return { images, videos, files };
  }, [messages]);

  const displayMessages = useMemo(() => {
    switch (activeTab) {
      case 'images':
        return mediaMessages.images;
      case 'videos':
        return mediaMessages.videos;
      case 'files':
        return mediaMessages.files;
      default:
        return [];
    }
  }, [activeTab, mediaMessages]);

  const handleDownload = async (message: Message) => {
    if (!message.file_url || !message.file_name || !message.file_type) return;

    try {
      setDownloading(message.id);
      await downloadFile(message.file_url, message.file_name, message.file_type);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleImagePress = (message: Message) => {
    if (message.type === 'image' && message.file_url) {
      router.push({
        pathname: '/image-viewer',
        params: { imageUrl: message.file_url },
      });
    }
  };

  const handlePollPress = (poll: PollWithDetails) => {
    // Navigate back to chat and scroll to poll message
    router.back();
  };

  const formatPollDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDeadline = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs < 0) return 'Verlopen';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 24) {
      return `Nog ${diffHours} uur`;
    }

    const diffDays = Math.floor(diffHours / 24);
    return `Nog ${diffDays} dagen`;
  };

  const renderMediaItem = ({ item }: { item: Message }) => {
    const isDownloading = downloading === item.id;

    if (item.type === 'image') {
      return (
        <Pressable
          onPress={() => handleImagePress(item)}
          onLongPress={() => handleDownload(item)}
          className="w-[32%] aspect-square rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 mb-2"
        >
          <Image
            source={{ uri: item.file_url }}
            className="w-full h-full"
            resizeMode="cover"
          />
          {isDownloading && (
            <View className="absolute inset-0 bg-black/50 items-center justify-center">
              <Loader size={24} />
            </View>
          )}
        </Pressable>
      );
    }

    if (item.type === 'video') {
      return (
        <Pressable
          onPress={() => handleDownload(item)}
          className="w-[32%] aspect-square rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 mb-2 items-center justify-center"
        >
          <Ionicons name="play-circle" size={48} color="#7C3AED" />
          {isDownloading && (
            <View className="absolute inset-0 bg-black/50 items-center justify-center">
              <Loader size={24} />
            </View>
          )}
        </Pressable>
      );
    }

    // Files
    return (
      <Pressable
        onPress={() => handleDownload(item)}
        className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-3 flex-row items-center"
      >
        <View className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 items-center justify-center mr-3">
          <Ionicons name="document" size={24} color="#7C3AED" />
        </View>
        <View className="flex-1">
          <Text
            className="text-base font-semibold text-gray-900 dark:text-white"
            numberOfLines={1}
          >
            {item.file_name || 'Unknown file'}
          </Text>
          <Text className="text-sm text-gray-500 dark:text-gray-400">
            {item.file_size ? `${(item.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
          </Text>
        </View>
        {isDownloading ? (
          <Loader size={20} />
        ) : (
          <Ionicons name="download-outline" size={24} color="#7C3AED" />
        )}
      </Pressable>
    );
  };

  const renderPollItem = (poll: PollWithDetails) => {
    const winningOption = poll.options.reduce(
      (max, opt) => (opt.vote_count > max.vote_count ? opt : max),
      poll.options[0]
    );

    return (
      <TouchableOpacity
        key={poll.id}
        onPress={() => handlePollPress(poll)}
        className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-3"
        activeOpacity={0.7}
      >
        <View className="flex-row items-start">
          <View className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 items-center justify-center mr-3">
            <Ionicons name="bar-chart" size={20} color="#7C3AED" />
          </View>
          <View className="flex-1">
            <Text
              className="text-base font-semibold text-gray-900 dark:text-white"
              numberOfLines={2}
            >
              {poll.question}
            </Text>
            <View className="flex-row items-center mt-1 flex-wrap">
              <Text className="text-sm text-gray-500 dark:text-gray-400">
                {poll.total_votes} {poll.total_votes === 1 ? 'stem' : 'stemmen'}
              </Text>
              <Text className="text-sm text-gray-400 dark:text-gray-500 mx-1">•</Text>
              <Text className="text-sm text-gray-500 dark:text-gray-400">
                {poll.options.length} opties
              </Text>
              {poll.closes_at && !poll.is_closed && (
                <>
                  <Text className="text-sm text-gray-400 dark:text-gray-500 mx-1">•</Text>
                  <Text className="text-sm text-orange-500">
                    {formatDeadline(poll.closes_at)}
                  </Text>
                </>
              )}
            </View>

            {/* Winner preview for closed polls */}
            {poll.is_closed && poll.total_votes > 0 && winningOption && (
              <View className="mt-2 flex-row items-center">
                <Ionicons name="trophy" size={14} color="#F59E0B" />
                <Text className="text-sm text-gray-700 dark:text-gray-300 ml-1" numberOfLines={1}>
                  {winningOption.option_text} ({winningOption.vote_count})
                </Text>
              </View>
            )}
          </View>

          {/* Status badge */}
          <View
            className={`px-2 py-1 rounded-full ${
              poll.is_closed
                ? 'bg-gray-200 dark:bg-gray-700'
                : poll.has_voted
                ? 'bg-green-100 dark:bg-green-900'
                : 'bg-purple-100 dark:bg-purple-900'
            }`}
          >
            <Text
              className={`text-xs font-medium ${
                poll.is_closed
                  ? 'text-gray-600 dark:text-gray-400'
                  : poll.has_voted
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-purple-700 dark:text-purple-400'
              }`}
            >
              {poll.is_closed ? 'Gesloten' : poll.has_voted ? 'Gestemd' : 'Open'}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderPollsContent = () => {
    if (polls.length === 0) {
      return (
        <View className="flex-1 items-center justify-center px-6 py-20">
          <Ionicons name="bar-chart-outline" size={64} color="#9CA3AF" />
          <Text className="text-gray-500 dark:text-gray-400 text-center mt-4 text-lg">
            Geen polls
          </Text>
          <Text className="text-gray-400 dark:text-gray-500 text-center mt-2">
            Maak een poll in de chat om te beginnen
          </Text>
        </View>
      );
    }

    return (
      <ScrollView className="flex-1 px-6 pt-4" showsVerticalScrollIndicator={false}>
        {activePolls.length > 0 && (
          <>
            <Text className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Actieve Polls ({activePolls.length})
            </Text>
            {activePolls.map(renderPollItem)}
          </>
        )}

        {closedPolls.length > 0 && (
          <>
            <Text className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 mt-4">
              Afgelopen Polls ({closedPolls.length})
            </Text>
            {closedPolls.map(renderPollItem)}
          </>
        )}

        <View className="h-6" />
      </ScrollView>
    );
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <SafeAreaView className="flex-1 bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <View className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <View className="flex-row items-center">
            <Pressable onPress={() => router.back()} className="mr-4">
              <Ionicons name="arrow-back" size={24} color="#7C3AED" />
            </Pressable>
            <Text className="text-xl font-bold text-gray-900 dark:text-white flex-1">
              Gedeelde Media
            </Text>
          </View>

          {/* Tabs - Scrollable for more tabs */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            className="mt-4"
            contentContainerStyle={{ paddingRight: 16 }}
          >
            <View className="flex-row bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
              <Pressable
                onPress={() => setActiveTab('images')}
                className={`px-4 py-2 rounded-lg items-center ${
                  activeTab === 'images' ? 'bg-white dark:bg-gray-600' : ''
                }`}
              >
                <Text
                  className={`font-semibold ${
                    activeTab === 'images'
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Foto's ({mediaMessages.images.length})
                </Text>
              </Pressable>

              <Pressable
                onPress={() => setActiveTab('videos')}
                className={`px-4 py-2 rounded-lg items-center ${
                  activeTab === 'videos' ? 'bg-white dark:bg-gray-600' : ''
                }`}
              >
                <Text
                  className={`font-semibold ${
                    activeTab === 'videos'
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Video's ({mediaMessages.videos.length})
                </Text>
              </Pressable>

              <Pressable
                onPress={() => setActiveTab('files')}
                className={`px-4 py-2 rounded-lg items-center ${
                  activeTab === 'files' ? 'bg-white dark:bg-gray-600' : ''
                }`}
              >
                <Text
                  className={`font-semibold ${
                    activeTab === 'files'
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Bestanden ({mediaMessages.files.length})
                </Text>
              </Pressable>

              <Pressable
                onPress={() => setActiveTab('polls')}
                className={`px-4 py-2 rounded-lg items-center ${
                  activeTab === 'polls' ? 'bg-white dark:bg-gray-600' : ''
                }`}
              >
                <Text
                  className={`font-semibold ${
                    activeTab === 'polls'
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Polls ({polls.length})
                </Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>

        {/* Content */}
        {loading ? (
          <View className="flex-1 items-center justify-center">
            <Loader size={60} />
          </View>
        ) : activeTab === 'polls' ? (
          renderPollsContent()
        ) : displayMessages.length === 0 ? (
          <View className="flex-1 items-center justify-center px-6">
            <Ionicons
              name={
                activeTab === 'images'
                  ? 'images-outline'
                  : activeTab === 'videos'
                  ? 'videocam-outline'
                  : 'document-outline'
              }
              size={64}
              color="#9CA3AF"
            />
            <Text className="text-gray-500 dark:text-gray-400 text-center mt-4 text-lg">
              Geen{' '}
              {activeTab === 'images' ? "foto's" : activeTab === 'videos' ? "video's" : 'bestanden'}{' '}
              gedeeld
            </Text>
            <Text className="text-gray-400 dark:text-gray-500 text-center mt-2">
              Gedeelde media verschijnt hier
            </Text>
          </View>
        ) : activeTab === 'files' ? (
          <ScrollView className="flex-1 px-6 pt-4">
            {displayMessages.map((message) => (
              <View key={message.id}>{renderMediaItem({ item: message })}</View>
            ))}
          </ScrollView>
        ) : (
          <FlatList
            data={displayMessages}
            renderItem={renderMediaItem}
            keyExtractor={(item) => item.id}
            numColumns={3}
            columnWrapperStyle={{
              justifyContent: 'space-between',
              paddingHorizontal: 24,
            }}
            contentContainerStyle={{ paddingTop: 16, paddingBottom: 24 }}
            showsVerticalScrollIndicator={false}
          />
        )}

        {/* Info footer */}
        {activeTab !== 'polls' && displayMessages.length > 0 && (
          <View className="bg-white dark:bg-gray-800 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Houd een item ingedrukt om te downloaden
            </Text>
          </View>
        )}
      </SafeAreaView>
    </>
  );
}
